
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

export function CustomerTable() {
  const { data: users, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          email,
          full_name,
          created_at,
          plan_type,
          subscribers (
            subscribed,
            subscription_tier,
            is_trial,
            is_admin_managed,
            subscription_end,
            trial_end
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching users:', error);
        throw error;
      }

      return data || [];
    }
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse text-gray-500">Loading users...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = (user: any) => {
    const subscriber = user.subscribers?.[0];
    
    if (subscriber?.is_admin_managed) {
      return <Badge variant="secondary">Admin Managed</Badge>;
    }
    if (subscriber?.is_trial) {
      return <Badge variant="outline">Trial</Badge>;
    }
    if (subscriber?.subscribed) {
      return <Badge variant="default">Premium</Badge>;
    }
    return <Badge variant="secondary">Free</Badge>;
  };

  const getPlanType = (user: any) => {
    const subscriber = user.subscribers?.[0];
    return subscriber?.subscription_tier || user.plan_type || 'Free';
  };

  const getExpiryDate = (user: any) => {
    const subscriber = user.subscribers?.[0];
    if (subscriber?.subscription_end) {
      return format(new Date(subscriber.subscription_end), 'MMM dd, yyyy');
    }
    if (subscriber?.trial_end) {
      return format(new Date(subscriber.trial_end), 'MMM dd, yyyy');
    }
    return '-';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>All Users ({users?.length || 0})</CardTitle>
      </CardHeader>
      <CardContent>
        {users && users.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Expires</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.full_name || 'N/A'}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{getStatusBadge(user)}</TableCell>
                    <TableCell>{getPlanType(user)}</TableCell>
                    <TableCell>
                      {format(new Date(user.created_at), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>
                      {getExpiryDate(user)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No users found
          </div>
        )}
      </CardContent>
    </Card>
  );
}
